import type { ObjectPhoto } from '@gmn-portal/backend';
import {
  ChevronLeft,
  ChevronRight,
  ImageOff,
  Images,
  Trash2,
  Undo2,
  Upload,
  X,
} from 'lucide-react';
import { nanoid } from 'nanoid';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  type ObjectPhotosFieldValue,
  type PendingObjectPhoto,
  useObjectPhotoBlobUrl,
  useObjectPhotos,
} from '@/core/api';
import { useIntl } from '@/core/i18n/use-intl';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Skeleton,
} from '@/shared/components/ui';
import { cn } from '@/shared/utils';

const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png'];
const MAX_UPLOAD_SIZE_BYTES = 20 * 1024 * 1024;

type PhotosFieldProps = {
  fieldKey: string;
  label?: string;
  /** Owning object; null while the object is being created. */
  objectId?: string | null;
  /**
   * Staged changes in edit mode. When `onChange` is set the field stages additions and
   * removals in the form; they are applied when the object is saved.
   */
  value?: ObjectPhotosFieldValue;
  onChange?: (value: ObjectPhotosFieldValue) => void;
  disabled?: boolean;
  preview?: boolean;
  className?: string;
};

type GalleryItem =
  | { kind: 'saved'; key: string; photo: ObjectPhoto; isRemoved: boolean }
  | { kind: 'pending'; key: string; pending: PendingObjectPhoto };

function SavedPhotoImage({
  photo,
  size,
  className,
}: {
  photo: ObjectPhoto;
  size: 'thumbnail' | 'full';
  className?: string;
}) {
  const { t } = useIntl();
  const { src, isError } = useObjectPhotoBlobUrl(photo, size);

  if (isError) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-1 bg-muted text-xs text-muted-foreground',
          className
        )}
      >
        <ImageOff className="size-5" />
        {t('metadata.photos.loadError')}
      </div>
    );
  }

  if (!src) {
    return <Skeleton className={className} />;
  }

  return <img src={src} alt={photo.filename || ''} className={className} />;
}

function PendingPhotoImage({ file, className }: { file: File; className?: string }) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!src) {
    return <Skeleton className={className} />;
  }

  return <img src={src} alt={file.name} className={className} />;
}

function GalleryImage({
  item,
  size,
  className,
}: {
  item: GalleryItem;
  size: 'thumbnail' | 'full';
  className?: string;
}) {
  if (item.kind === 'pending') {
    return <PendingPhotoImage file={item.pending.file} className={className} />;
  }
  return <SavedPhotoImage photo={item.photo} size={size} className={className} />;
}

export function PhotosField({
  fieldKey,
  label,
  objectId,
  value,
  onChange,
  disabled = false,
  preview = false,
  className,
}: PhotosFieldProps) {
  const { t } = useIntl();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const canEdit = Boolean(onChange) && !preview && !disabled;
  const { data, isLoading, isError } = useObjectPhotos(objectId, fieldKey, { disabled: preview });

  const added = value?.added ?? [];
  const removed = value?.removed ?? [];
  const items: GalleryItem[] = [
    ...(data?.docs ?? []).map(
      (photo): GalleryItem => ({
        kind: 'saved',
        key: photo.id,
        photo,
        isRemoved: removed.includes(photo.id),
      })
    ),
    ...added.map((pending): GalleryItem => ({ kind: 'pending', key: pending.id, pending })),
  ];
  const visibleCount = items.filter((item) => item.kind === 'pending' || !item.isRemoved).length;
  const activeItem = activeIndex !== null ? (items[activeIndex] ?? null) : null;
  const title = label || t('metadata.fieldTypes.photos');

  const handleFiles = (files: File[]) => {
    const accepted: PendingObjectPhoto[] = [];
    for (const file of files) {
      if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
        toast.error(t('metadata.photos.invalidType', { name: file.name }));
        continue;
      }
      if (file.size > MAX_UPLOAD_SIZE_BYTES) {
        toast.error(t('operations.toasts.fileTooLarge'));
        continue;
      }
      accepted.push({ id: nanoid(10), file });
    }
    if (accepted.length > 0) {
      onChange?.({ added: [...added, ...accepted], removed });
    }
  };

  const toggleRemoved = (photoId: string) =>
    onChange?.({
      added,
      removed: removed.includes(photoId)
        ? removed.filter((id) => id !== photoId)
        : [...removed, photoId],
    });

  const discardPending = (pendingId: string) =>
    onChange?.({ added: added.filter((entry) => entry.id !== pendingId), removed });

  const step = (direction: 1 | -1) =>
    setActiveIndex((index) =>
      index === null ? null : (index + direction + items.length) % Math.max(items.length, 1)
    );

  const renderTileAction = (item: GalleryItem) => {
    if (!canEdit) return null;

    const isUndo = item.kind === 'saved' && item.isRemoved;
    const Icon = item.kind === 'pending' ? X : isUndo ? Undo2 : Trash2;
    const labelKey =
      item.kind === 'pending'
        ? 'metadata.photos.discard'
        : isUndo
          ? 'metadata.photos.undoRemove'
          : 'metadata.photos.delete';

    return (
      <Button
        type="button"
        variant="secondary"
        size="icon"
        className="absolute right-2 top-2 size-8 shadow-sm"
        aria-label={t(labelKey)}
        onClick={() =>
          item.kind === 'pending' ? discardPending(item.key) : toggleRemoved(item.photo.id)
        }
        data-testid={`photos-field-tile-action-${fieldKey}`}
      >
        <Icon className="size-4" />
      </Button>
    );
  };

  const renderTileBadge = (item: GalleryItem) => {
    if (item.kind === 'pending') {
      return (
        <Badge className="pointer-events-none absolute left-2 top-2">
          {t('metadata.photos.pendingAdd')}
        </Badge>
      );
    }
    if (item.isRemoved) {
      return (
        <Badge variant="destructive" className="pointer-events-none absolute left-2 top-2">
          {t('metadata.photos.pendingRemove')}
        </Badge>
      );
    }
    return null;
  };

  const renderBody = () => {
    if (preview) {
      return <div className="text-sm text-muted-foreground">{t('metadata.photos.preview')}</div>;
    }
    if (objectId && isLoading) {
      return (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          <Skeleton className="aspect-[4/3] w-full rounded-lg" />
          <Skeleton className="aspect-[4/3] w-full rounded-lg" />
        </div>
      );
    }
    if (objectId && isError) {
      return (
        <div className="rounded-lg border border-dashed border-destructive/40 bg-destructive/5 p-5 text-sm text-destructive">
          {t('metadata.photos.listError')}
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-5 text-sm text-muted-foreground">
          {t('metadata.photos.empty')}
        </div>
      );
    }
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((item, index) => (
          <div
            key={item.key}
            className={cn(
              'group relative overflow-hidden rounded-lg border bg-muted',
              item.kind === 'pending' ? 'border-dashed border-primary' : 'border-border'
            )}
            data-testid={`photos-field-item-${fieldKey}`}
            data-state={item.kind === 'pending' ? 'pending' : item.isRemoved ? 'removed' : 'saved'}
          >
            <button
              type="button"
              onClick={() => setActiveIndex(index)}
              className="block w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t('metadata.photos.open', { index: index + 1 })}
            >
              <GalleryImage
                item={item}
                size="thumbnail"
                className={cn(
                  'aspect-[4/3] w-full object-cover transition-transform group-hover:scale-[1.02]',
                  item.kind === 'saved' && item.isRemoved && 'opacity-40 grayscale'
                )}
              />
            </button>
            {renderTileBadge(item)}
            {renderTileAction(item)}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      className={cn('rounded-xl border border-border bg-card p-4 space-y-3', className)}
      data-testid={`photos-field-${fieldKey}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Images className="size-4 text-muted-foreground" />
          <span>{title}</span>
          {visibleCount > 0 ? (
            <span className="text-muted-foreground">({visibleCount})</span>
          ) : null}
        </div>
        {canEdit ? (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              data-testid={`photos-field-upload-${fieldKey}`}
            >
              <Upload className="mr-2 size-4" />
              {t('metadata.photos.upload')}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_MIME_TYPES.join(',')}
              multiple
              className="hidden"
              data-testid={`photos-field-input-${fieldKey}`}
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []);
                event.currentTarget.value = '';
                handleFiles(files);
              }}
            />
          </>
        ) : null}
      </div>

      {renderBody()}

      {canEdit ? (
        <div className="text-xs text-muted-foreground">{t('metadata.photos.uploadHint')}</div>
      ) : null}

      <Dialog open={activeItem !== null} onOpenChange={(open) => !open && setActiveIndex(null)}>
        <DialogContent className="sm:max-w-5xl" data-testid={`photos-field-lightbox-${fieldKey}`}>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {activeIndex !== null
              ? t('metadata.photos.counter', { index: activeIndex + 1, total: items.length })
              : null}
          </DialogDescription>
          {activeItem ? (
            <div className="relative flex items-center justify-center rounded-lg bg-muted">
              <GalleryImage
                key={activeItem.key}
                item={activeItem}
                size="full"
                className="max-h-[70vh] min-h-48 w-full rounded-lg object-contain"
              />
              {items.length > 1 ? (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className="absolute left-2 top-1/2 -translate-y-1/2"
                    onClick={() => step(-1)}
                    aria-label={t('metadata.photos.previous')}
                  >
                    <ChevronLeft className="size-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={() => step(1)}
                    aria-label={t('metadata.photos.next')}
                  >
                    <ChevronRight className="size-4" />
                  </Button>
                </>
              ) : null}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
